const REVIEW_PHOTO_ZOOM_MIN = 1;
const REVIEW_PHOTO_ZOOM_MAX = 5;
const REVIEW_PHOTO_TAP_SLOP = 10;

function stepPhotoIndex(index, delta, length) {
  const n = Number(length);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const i = Number(index) || 0;
  const d = Number(delta) || 0;
  return ((i + d) % n + n) % n;
}

function reviewPhotoIsZoomed(scale) {
  return Number(scale) > REVIEW_PHOTO_ZOOM_MIN;
}

function clampReviewPhotoScale(scale) {
  const n = Number(scale);
  if (!Number.isFinite(n) || n <= REVIEW_PHOTO_ZOOM_MIN) return REVIEW_PHOTO_ZOOM_MIN;
  if (n >= REVIEW_PHOTO_ZOOM_MAX) return REVIEW_PHOTO_ZOOM_MAX;
  return n;
}

function reviewPhotoZoomState(scale, x, y) {
  const next = clampReviewPhotoScale(scale);
  if (!reviewPhotoIsZoomed(next)) return { scale: REVIEW_PHOTO_ZOOM_MIN, x: 0, y: 0 };
  return { scale: next, x: Number(x) || 0, y: Number(y) || 0 };
}

function reviewPhotoPointDistance(a, b) {
  return Math.hypot(
    (Number(b?.clientX) || 0) - (Number(a?.clientX) || 0),
    (Number(b?.clientY) || 0) - (Number(a?.clientY) || 0),
  );
}

function reviewPhotoPinchScale(startScale, startDistance, distance) {
  const start = Number(startDistance);
  const current = Number(distance);
  if (!start || !current) return clampReviewPhotoScale(startScale);
  return clampReviewPhotoScale((Number(startScale) || 1) * (current / start));
}

function startReviewPhotoGesture(points, state) {
  const current = reviewPhotoZoomState(state?.scale, state?.x, state?.y);
  const list = Array.isArray(points) ? points : [];
  const point = list[0] || { clientX: 0, clientY: 0 };
  if (list.length >= 2) {
    return {
      mode: "pinch",
      pinching: true,
      moved: false,
      startScale: current.scale,
      startDistance: reviewPhotoPointDistance(list[0], list[1]),
      lastX: point.clientX,
      lastY: point.clientY,
      originX: point.clientX,
      originY: point.clientY,
    };
  }
  return {
    mode: reviewPhotoIsZoomed(current.scale) ? "pan" : "idle",
    pinching: false,
    moved: false,
    startScale: current.scale,
    startDistance: 0,
    lastX: point.clientX,
    lastY: point.clientY,
    originX: point.clientX,
    originY: point.clientY,
  };
}

function updateReviewPhotoGesture(gesture, points, state) {
  const current = reviewPhotoZoomState(state?.scale, state?.x, state?.y);
  const list = Array.isArray(points) ? points : [];
  const active = gesture || startReviewPhotoGesture(list, current);
  if (list.length >= 2) {
    const distance = reviewPhotoPointDistance(list[0], list[1]);
    const pinching =
      active.mode === "pinch"
        ? active
        : {
            ...active,
            mode: "pinch",
            pinching: true,
            moved: true,
            startScale: current.scale,
            startDistance: distance,
          };
    const scale =
      active.mode === "pinch"
        ? reviewPhotoPinchScale(pinching.startScale, pinching.startDistance, distance)
        : current.scale;
    return {
      state: reviewPhotoZoomState(scale, current.x, current.y),
      gesture: { ...pinching, pinching: true, mode: "pinch" },
    };
  }
  if (list.length === 1) {
    const point = list[0];
    const traveled = Math.hypot(point.clientX - active.originX, point.clientY - active.originY);
    const moved = active.moved || traveled > REVIEW_PHOTO_TAP_SLOP;
    if (active.mode !== "pinch" && reviewPhotoIsZoomed(current.scale)) {
      const panning = active.moved || traveled > REVIEW_PHOTO_TAP_SLOP;
      return {
        state: panning
          ? reviewPhotoZoomState(
              current.scale,
              current.x + (point.clientX - active.lastX),
              current.y + (point.clientY - active.lastY),
            )
          : current,
        gesture: {
          ...active,
          mode: "pan",
          moved: panning,
          lastX: panning ? point.clientX : active.lastX,
          lastY: panning ? point.clientY : active.lastY,
        },
      };
    }
    return {
      state: current,
      gesture: {
        ...active,
        moved,
        lastX: point.clientX,
        lastY: point.clientY,
      },
    };
  }
  return { state: current, gesture: active };
}

function finishReviewPhotoGesture(gesture, points, state) {
  const current = reviewPhotoZoomState(state?.scale, state?.x, state?.y);
  const list = Array.isArray(points) ? points : [];
  if (!gesture) {
    return { state: current, gesture: null, tapped: false, ignoreClick: false };
  }
  if (list.length > 0) {
    const point = list[0];
    return {
      state: current,
      gesture: {
        ...gesture,
        mode: reviewPhotoIsZoomed(current.scale) ? "pan" : "idle",
        moved: true,
        lastX: point.clientX,
        lastY: point.clientY,
        originX: point.clientX,
        originY: point.clientY,
      },
      tapped: false,
      ignoreClick: true,
    };
  }
  const tapped =
    !gesture.pinching &&
    gesture.mode !== "pinch" &&
    !gesture.moved &&
    reviewPhotoIsZoomed(current.scale);
  return {
    state: tapped ? reviewPhotoZoomState(1, 0, 0) : current,
    gesture: null,
    tapped,
    ignoreClick: !!(gesture.pinching || gesture.moved || gesture.mode === "pinch"),
  };
}

function reviewPhotoCaption(photo) {
  return String(photo?.caption || "").trim();
}

function rememberedReviewPhotos(sets, workOrderId) {
  if (workOrderId == null || workOrderId === "" || !sets) return undefined;
  const key = String(workOrderId);
  if (!Object.prototype.hasOwnProperty.call(sets, key)) return undefined;
  return sets[key];
}

function mergeDisplayedPhotos(painted, manifest, readyById) {
  const paintedById = new Map();
  for (const photo of painted || []) {
    if (photo && photo.id != null) paintedById.set(String(photo.id), photo);
  }
  const ready = readyById instanceof Map ? readyById : new Map();
  const out = [];
  for (const item of manifest || []) {
    if (!item || item.id == null) continue;
    const prev = paintedById.get(String(item.id));
    if (prev) {
      out.push({
        ...prev,
        id: prev.id,
        caption: item.caption != null ? item.caption : prev.caption || "",
      });
      continue;
    }
    const next =
      ready.get(item.id) ?? ready.get(String(item.id)) ?? ready.get(Number(item.id));
    if (next) out.push({ ...item, ...next, id: item.id });
  }
  return out;
}

function sameReviewPhotoSet(left, right) {
  const a = Array.isArray(left) ? left : null;
  const b = Array.isArray(right) ? right : null;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((photo, index) => {
    const other = b[index];
    return (
      photo &&
      other &&
      String(photo.id) === String(other.id) &&
      String(photo.caption || "") === String(other.caption || "")
    );
  });
}

function isDisplayablePhotoBlob(blob) {
  if (!blob || typeof blob.size !== "number" || blob.size <= 0) return false;
  const type = String(blob.type || "");
  if (/json|text\/html|text\/plain/i.test(type)) return false;
  return true;
}

/**
 * Return photo rows with a displayable `blob`.
 * `read` supplies a cached blob. A miss calls `fetchBlob` and `write`.
 * Files are read one at a time. `onPhoto` receives the photos ready so far
 * so one slow file does not hide the ones that already arrived.
 */
async function resolveWorkOrderPhotoBlobs(photos, hooks) {
  const { read, write, fetchBlob, onPhoto } = hooks || {};
  const list = Array.isArray(photos) ? photos : [];
  const resolved = new Array(list.length);
  let cursor = 0;

  async function one(photo) {
    if (!photo || photo.id == null) return null;
    let blob;
    try {
      blob = await read(photo.id);
    } catch {
      blob = undefined;
    }
    if (!isDisplayablePhotoBlob(blob)) {
      try {
        blob = await fetchBlob(photo.id);
      } catch {
        return null;
      }
      if (!isDisplayablePhotoBlob(blob)) return null;
      try {
        await write(photo.id, blob);
      } catch {
        /* show the photo even if the device cache rejects the write */
      }
    }
    return { ...photo, blob };
  }

  while (cursor < list.length) {
    const index = cursor;
    cursor += 1;
    const photo = await one(list[index]);
    resolved[index] = photo;
    if (photo && typeof onPhoto === "function") {
      await onPhoto(resolved.filter(Boolean));
    }
  }
  return resolved.filter(Boolean);
}

module.exports = {
  REVIEW_PHOTO_ZOOM_MIN,
  REVIEW_PHOTO_ZOOM_MAX,
  REVIEW_PHOTO_TAP_SLOP,
  stepPhotoIndex,
  reviewPhotoCaption,
  reviewPhotoIsZoomed,
  clampReviewPhotoScale,
  reviewPhotoZoomState,
  reviewPhotoPinchScale,
  startReviewPhotoGesture,
  updateReviewPhotoGesture,
  finishReviewPhotoGesture,
  rememberedReviewPhotos,
  mergeDisplayedPhotos,
  sameReviewPhotoSet,
  resolveWorkOrderPhotoBlobs,
};
